import * as React from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import {
  ArrowRight,
  Building2,
  CreditCard,
  FileText,
  LayoutDashboard,
  LineChart,
  LogOut,
  Plus,
  Settings,
  ShieldCheck,
  Users,
  Activity,
  Plug,
  History,
  Search,
  Store,
} from 'lucide-react';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandShortcut,
} from '@/components/ui/command';
import { useAuthStore } from '@/stores/auth';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';

const isMac =
  typeof navigator !== 'undefined' &&
  /Mac|iPhone|iPod|iPad/.test(navigator.userAgent);

type Item = {
  id: string;
  title: string;
  description?: string;
  icon: React.ComponentType<{ className?: string }>;
  shortcut?: string;
  group: 'navigation' | 'actions' | 'recent';
  keywords?: string[];
  perform: () => void;
};

const RECENT_KEY = 'cms-central-command-recent';
const MAX_RECENT = 5;

export function CommandPalette() {
  const [open, setOpen] = React.useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, logout } = useAuthStore();

  const { data: sites } = useQuery({
    queryKey: ['sites-search'],
    queryFn: () => api.get<{ items: Array<{ id: string; name: string }> }>('/sites', { query: { limit: 8 } }),
    enabled: open,
    staleTime: 60 * 1000,
  });

  const items: Item[] = React.useMemo(
    () => [
      {
        id: 'nav-dashboard',
        title: t('nav.dashboard'),
        icon: LayoutDashboard,
        group: 'navigation',
        keywords: ['panel', 'inicio', 'home'],
        perform: () => navigate({ to: '/dashboard' }),
      },
      {
        id: 'nav-sites',
        title: t('nav.sites'),
        icon: Building2,
        group: 'navigation',
        keywords: ['sitios'],
        perform: () => navigate({ to: '/sites' }),
      },
      {
        id: 'nav-users',
        title: t('nav.users'),
        icon: Users,
        group: 'navigation',
        keywords: ['usuarios'],
        perform: () => navigate({ to: '/users' }),
      },
      {
        id: 'nav-billing',
        title: t('nav.billing'),
        icon: CreditCard,
        group: 'navigation',
        keywords: ['facturacion', 'pagos'],
        perform: () => navigate({ to: '/billing' }),
      },
      {
        id: 'nav-marketplace',
        title: t('nav.marketplace'),
        icon: Store,
        group: 'navigation',
        keywords: ['mercado'],
        perform: () => navigate({ to: '/marketplace' }),
      },
      {
        id: 'nav-status',
        title: t('nav.status'),
        icon: Activity,
        group: 'navigation',
        keywords: ['estado', 'status'],
        perform: () => navigate({ to: '/status' }),
      },
      {
        id: 'nav-settings',
        title: t('nav.settings'),
        icon: Settings,
        group: 'navigation',
        keywords: ['ajustes', 'configuracion'],
        perform: () => navigate({ to: '/settings' }),
      },
      {
        id: 'nav-audit',
        title: t('nav.audit'),
        icon: History,
        group: 'navigation',
        keywords: ['auditoria', 'log'],
        perform: () => navigate({ to: '/audit-logs' }),
      },
      {
        id: 'action-new-site',
        title: 'Nuevo sitio',
        icon: Plus,
        group: 'actions',
        shortcut: 'N S',
        keywords: ['crear', 'site'],
        perform: () => navigate({ to: '/sites', search: { new: 1 } }),
      },
      {
        id: 'action-logout',
        title: t('nav.logout'),
        icon: LogOut,
        group: 'actions',
        keywords: ['salir', 'cerrar'],
        perform: () => {
          logout();
          navigate({ to: '/login' });
        },
      },
      ...(sites?.items ?? []).map<Item>((s) => ({
        id: `site-${s.id}`,
        title: s.name,
        description: 'Ir al sitio',
        icon: Building2,
        group: 'navigation',
        perform: () => navigate({ to: '/sites/$siteId', params: { siteId: s.id } }),
      })),
    ],
    [navigate, t, sites, logout],
  );

  React.useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if ((isMac ? e.metaKey : e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener('keydown', down);
    return () => window.removeEventListener('keydown', down);
  }, []);

  const run = (item: Item) => {
    try {
      const raw = localStorage.getItem(RECENT_KEY);
      const recent: string[] = raw ? (JSON.parse(raw) as string[]) : [];
      const next = [item.id, ...recent.filter((id) => id !== item.id)].slice(0, MAX_RECENT);
      localStorage.setItem(RECENT_KEY, JSON.stringify(next));
    } catch {
      /* ignore */
    }
    item.perform();
    setOpen(false);
  };

  const nav = items.filter((i) => i.group === 'navigation');
  const actions = items.filter((i) => i.group === 'actions');

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput
        placeholder={t('command.placeholder')}
        aria-label={t('command.placeholder')}
      />
      <CommandList>
        <CommandEmpty>
          <div className="flex flex-col items-center gap-2">
            <Search className="h-6 w-6 text-muted-foreground" />
            <p>{t('command.empty')}</p>
          </div>
        </CommandEmpty>
        <CommandGroup heading={t('command.navigation')}>
          {nav.map((item) => (
            <CommandItem key={item.id} onSelect={() => run(item)} value={item.title + ' ' + (item.keywords ?? []).join(' ')}>
              <item.icon className="text-muted-foreground" />
              <span>{item.title}</span>
              {item.description && (
                <span className="ml-auto text-xs text-muted-foreground">
                  {item.description}
                </span>
              )}
              <ArrowRight className="ml-2 h-3 w-3 text-muted-foreground" />
            </CommandItem>
          ))}
        </CommandGroup>
        <CommandGroup heading={t('command.actions')}>
          {actions.map((item) => (
            <CommandItem key={item.id} onSelect={() => run(item)} value={item.title + ' ' + (item.keywords ?? []).join(' ')}>
              <item.icon className="text-muted-foreground" />
              <span>{item.title}</span>
              {item.shortcut && <CommandShortcut>{item.shortcut}</CommandShortcut>}
            </CommandItem>
          ))}
        </CommandGroup>
      </CommandList>
      <div className="border-t px-3 py-2 text-xs text-muted-foreground flex items-center justify-between">
        <span>
          {user?.displayName ?? user?.email} · {user?.role}
        </span>
        <span>
          {t('command.footer', { key: isMac ? '⌘K' : 'Ctrl+K' })}
        </span>
      </div>
    </CommandDialog>
  );
}

export const __unused = { LineChart, ShieldCheck, FileText, Plug };
